import assertNever from '@watershed/shared-util/src/assertNever';
import must from '../utils/must';
import { ASTExpr, ASTString, stringifyExpr } from './ast';

export type LiteralType = string | number | boolean | null;

export function isLiteral(expr: ASTExpr): boolean {
  switch (expr.t) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'null':
      return true;
    case 'unary':
      return isLiteral(expr.expr);
    case 'identifier':
    case 'binary':
    case 'comparison':
    case 'call':
      return false;

    default:
      assertNever(expr);
  }
}

export function isNumberLiteral(expr: ASTExpr): boolean {
  switch (expr.t) {
    case 'number':
      return true;
    case 'unary':
      return isLiteral(expr.expr);
    case 'string':
    case 'boolean':
    case 'null':
    case 'identifier':
    case 'binary':
    case 'comparison':
    case 'call':
      return false;
    default:
      assertNever(expr);
  }
}

export function extractLiteral(expr: ASTExpr): LiteralType {
  switch (expr.t) {
    case 'string':
    case 'number':
    case 'boolean':
      return expr.v;
    case 'unary':
      switch (expr.op) {
        case '-':
          return -must(extractLiteral(expr.expr), 'Cannot negate null!');
        default:
          assertNever(expr);
      }
    case 'identifier':
    case 'binary':
    case 'comparison':
    case 'call':
      throw new TypeError(
        `Expected ${stringifyExpr(
          expr
        )} to be a literal value (i.e., a hardcoded string, number, or boolean)`
      );

    case 'null':
      return null;

    default:
      assertNever(expr);
  }
}

export function extractStringLiteral(expr: ASTExpr): string {
  if (expr.t !== 'string') {
    throw new TypeError(
      `Expression ${stringifyExpr(expr)} should be a string literal`
    );
  }
  return expr.v;
}

export function extractNumberLiteral(expr: ASTExpr): number {
  if (expr.t === 'unary' && expr.op === '-') {
    return -extractNumberLiteral(expr.expr);
  }
  if (expr.t !== 'number') {
    throw new TypeError(
      `Expression ${stringifyExpr(expr)} should be a number literal`
    );
  }
  return expr.v;
}

export function stringLiteral(v: string): ASTString {
  return { t: 'string', v };
}
